
import React, { useState, useRef, useEffect } from 'react';
import { X, Upload, Link as LinkIcon, Move, ZoomIn, Save, Loader2, Check, MousePointer2 } from 'lucide-react';
import { uploadToCloudinary } from '../utils/fileHelpers';

interface ImageEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialUrl: string;
  initialScale?: number;
  initialPosition?: string;
  onSave: (data: { url: string; scale: number; position: string }) => Promise<void>;
  title?: string;
  aspectRatio?: 'square' | 'video' | 'banner';
}

export const ImageEditorModal: React.FC<ImageEditorModalProps> = ({
  isOpen,
  onClose,
  initialUrl,
  initialScale = 1,
  initialPosition = '50% 50%',
  onSave,
  title = "Adjust Image",
  aspectRatio = 'square'
}) => {
  const [currentUrl, setCurrentUrl] = useState(initialUrl);
  const [urlInput, setUrlInput] = useState('');
  const [scale, setScale] = useState(initialScale);
  
  // posX/posY here are offsets from center (50% 50%)
  const [posX, setPosX] = useState(0);
  const [posY, setPosY] = useState(0);
  
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDraggingImage, setIsDraggingImage] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef({ x: 0, y: 0, startPosX: 0, startPosY: 0 });
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setCurrentUrl(initialUrl);
      setScale(initialScale);
      const pos = initialPosition.split(' ');
      // Convert "X% Y%" to offset from center
      const currentX = parseFloat(pos[0]) || 50;
      const currentY = parseFloat(pos[1]) || 50;
      setPosX(currentX - 50);
      setPosY(currentY - 50);
      setPendingFile(null);
      setPreviewUrl(null);
      setUrlInput('');
    }
  }, [isOpen, initialUrl, initialScale, initialPosition]);

  const handleFileSelection = (file: File) => {
    setPendingFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreviewUrl(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDraggingImage(true);
    dragStartRef.current = { 
      x: e.clientX, 
      y: e.clientY, 
      startPosX: posX, 
      startPosY: posY 
    };
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingImage || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaY = e.clientY - dragStartRef.current.y;
      
      // Calculate move in percentage of container
      // This allows 2D movement regardless of image aspect ratio
      const factorX = (deltaX / rect.width) * 100;
      const factorY = (deltaY / rect.height) * 100;
      
      setPosX(dragStartRef.current.startPosX + factorX);
      setPosY(dragStartRef.current.startPosY + factorY);
    };

    const handleMouseUp = () => setIsDraggingImage(false);

    if (isDraggingImage) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingImage, posX, posY]);

  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const file = items[i].getAsFile();
          if (file) handleFileSelection(file);
        }
      }
    };

    if (isOpen) {
      window.addEventListener('paste', handlePaste);
      return () => window.removeEventListener('paste', handlePaste);
    }
  }, [isOpen]);

  const handleSave = async () => {
    setIsUploading(true);
    try {
      let finalUrl = currentUrl;
      if (pendingFile) {
        finalUrl = await uploadToCloudinary(pendingFile);
      } else if (urlInput) {
        finalUrl = urlInput;
      }
      
      await onSave({
        url: finalUrl,
        scale,
        // Save back as X% Y% (centered at 50%)
        position: `${posX + 50}% ${posY + 50}%`
      });
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  const displayUrl = previewUrl || currentUrl;
  
  const ratioClasses = {
    square: 'aspect-square max-h-[450px]',
    video: 'aspect-video max-h-[450px]',
    banner: 'aspect-[21/9] max-h-[450px]'
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/90 backdrop-blur-xl p-4 animate-in fade-in duration-300">
      <div className="bg-white rounded-[3rem] shadow-2xl w-full max-w-5xl flex flex-col lg:flex-row overflow-hidden border border-white/20">
        
        {/* Left: Interactive Visual Portal */}
        <div className="flex-1 bg-slate-100 flex items-center justify-center p-6 lg:p-12">
           <div className="w-full relative group">
              <div 
                ref={containerRef}
                className={`w-full mx-auto overflow-hidden rounded-3xl bg-slate-200 shadow-2xl cursor-move relative border-8 ${isDraggingImage ? 'border-primary-500 scale-[0.99]' : 'border-white'} transition-all duration-200 ${ratioClasses[aspectRatio]}`}
                onMouseDown={handleMouseDown}
              >
                  <img 
                    src={displayUrl || undefined} 
                    alt="Viewport" 
                    className="w-full h-full pointer-events-none select-none origin-center"
                    style={{ 
                      objectFit: 'cover', 
                      // Using transform for 2D freedom (X + Y)
                      transform: `translate(${posX}%, ${posY}%) scale(${scale})`
                    }}
                  />
                  
                  {/* Composition Grid */}
                  <div className="absolute inset-0 pointer-events-none grid grid-cols-3 grid-rows-3 opacity-20 group-hover:opacity-40 transition-opacity">
                     {[...Array(9)].map((_, i) => (
                       <div key={i} className="border border-white/50"></div>
                     ))}
                  </div>

                  <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                     <div className="bg-white/90 backdrop-blur px-5 py-2.5 rounded-full shadow-xl flex items-center gap-3 text-xs font-black uppercase tracking-widest text-slate-900">
                        <MousePointer2 size={16} /> 2D Free Move
                     </div>
                  </div>
              </div>

              {/* Zoom Slider */}
              <div className="mt-8 bg-white/60 backdrop-blur-xl px-6 py-5 rounded-3xl border border-white shadow-lg flex items-center gap-6">
                <div className="p-2 bg-white rounded-xl shadow-sm text-slate-400"><ZoomIn size={20} /></div>
                <input 
                  type="range" min="0.5" max="5" step="0.01" 
                  value={scale} 
                  onChange={(e) => setScale(parseFloat(e.target.value))}
                  className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
                />
                <div className="w-20 text-right font-mono font-bold text-slate-600 text-sm">{Math.round(scale * 100)}%</div>
              </div>
           </div>
        </div>

        {/* Right: Asset Configuration */}
        <div className="w-full lg:w-[400px] p-8 lg:p-12 flex flex-col border-l border-slate-50 bg-white">
          <div className="flex justify-between items-start mb-10">
             <div>
                <h2 className="text-2xl font-black font-display text-slate-900 tracking-tight">{title}</h2>
                <p className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em] mt-2">Visual Editor v2.0</p>
             </div>
             <button onClick={onClose} className="p-2.5 hover:bg-slate-50 rounded-full transition-all hover:rotate-90"><X size={24} className="text-slate-300" /></button>
          </div>

          <div className="space-y-8 flex-1">
             <div 
               onClick={() => fileInputRef.current?.click()}
               className={`group border-2 border-dashed rounded-[2.5rem] p-10 flex flex-col items-center justify-center text-center cursor-pointer transition-all duration-500 ${pendingFile ? 'border-primary-500 bg-primary-50/30' : 'border-slate-100 hover:border-primary-400 hover:bg-slate-50'}`}
             >
                <input 
                  type="file" ref={fileInputRef} className="hidden" accept="image/*" 
                  onChange={(e) => e.target.files?.[0] && handleFileSelection(e.target.files[0])} 
                />
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 shadow-sm transition-all duration-500 ${pendingFile ? 'bg-primary-500 text-white scale-110' : 'bg-white text-slate-300 group-hover:text-primary-500'}`}>
                   {pendingFile ? <Check size={28} /> : <Upload size={28} />}
                </div>
                <p className="text-sm font-black text-slate-900 leading-tight">
                  {pendingFile ? pendingFile.name : "Paste (Ctrl+V) or Drag"}
                </p>
             </div>

             <div className="relative">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-100"></div></div>
                <div className="relative flex justify-center"><span className="bg-white px-5 text-[10px] font-black text-slate-300 uppercase tracking-[0.3em]">URL Injection</span></div>
             </div>

             <div className="relative group">
                <LinkIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-primary-500 transition-colors" size={18} />
                <input 
                  type="text" 
                  placeholder="Paste URL..." 
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-sm font-bold focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all"
                />
             </div>
          </div>

          <button 
            onClick={handleSave}
            disabled={isUploading}
            className="mt-12 w-full bg-slate-900 hover:bg-primary-600 text-white py-5 rounded-2xl font-black uppercase tracking-[0.2em] text-xs shadow-2xl shadow-slate-900/20 transition-all flex items-center justify-center gap-4 disabled:opacity-50 active:scale-[0.98]"
          >
            {isUploading ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
            Confirm Adjustments
          </button>
        </div>
      </div>
    </div>
  );
};
